import React, { Suspense, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import Dropzone from "react-dropzone";
import AvatarEditor from "react-avatar-editor";
import { IUserData } from "@/types";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { IconX } from "@tabler/icons-react";
import { SetProfilePic } from "@/lib/api";
import * as Impersonate from "@/lib/impersonate_api";
import { cn } from "@/lib/utils";
interface Props {
    user: IUserData;
    admin?: boolean;
    className?: string;
    impersonate?: boolean;
    reload?: boolean;
    setOpen?: (open: boolean) => void;
    customTrigger?: React.ReactNode;
    cc?: boolean;
}

const ImageCropper: React.FC<Props> = ({
    user,
    admin,
    impersonate,
    reload,
    setOpen,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
    const [editor, setEditor] = useState<AvatarEditor | null>(null);
    const [image, setImage] = useState<File | null>(null);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const handleScale = (value: number[]) => {
        setScale(value[0]);
    };

    const handlePositionChange = (position: { x: number; y: number }) => {
        setPosition(position);
    };

    const handleImageLoad = (image: HTMLImageElement | null) => {
        if (image) {
            const { width, height } = image;
            setPosition({ x: width / 2, y: height / 2 });
        }
    };

    const handleSave = async () => {
        if (editor) {
            const canvas = editor.getImageScaledToCanvas();
            // Convert canvas to Blob with desired type and quality
            return new Promise((resolve) => {
                canvas.toBlob(
                    (blob) => {
                        resolve(blob);
                    },
                    "image/webp",
                    0.5
                );
            });
        }
    };

    const handleDrop = (acceptedFiles: File[]) => {
        setImage(acceptedFiles[0]);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setImage(event.target.files[0]);
        }
    };

    const handleContainerClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
    };

    return (
        <div className="relative flex flex-col gap-3 px-3 pt-3">
            <Dropzone onDrop={handleDrop} noClick noKeyboard>
                {({ getRootProps, getInputProps }) => (
                    <div
                        {...getRootProps()}
                        onClick={handleContainerClick}
                        className="flex h-full w-full flex-col items-center justify-center rounded-md border-4 border-dashed bg-card"
                        // style={{
                        //     width: "250px",
                        //     height: "250px",
                        // }}
                    >
                        {image ? (
                            <div className="relative rounded-md">
                                {/* @ts-ignore */}
                                <AvatarEditor
                                    ref={(ref) => {
                                        setEditor(ref);
                                    }}
                                    image={image}
                                    width={350}
                                    height={350}
                                    border={0}
                                    // @ts-ignore
                                    color={["transparent"]}
                                    scale={scale}
                                    position={position}
                                    // @ts-ignore
                                    onImageReady={handleImageLoad}
                                    // @ts-ignore
                                    onLoadSuccess={handleImageLoad}
                                    onPositionChange={handlePositionChange}
                                    className="overflow-hidden rounded-md"
                                />
                                <Button
                                    className="absolute right-2 top-2 h-auto w-auto rounded-full"
                                    size="icon"
                                    onClick={handleRemoveImage}
                                    aria-label="Remove image"
                                >
                                    <IconX className="h-6 w-6 p-1 text-white" />
                                </Button>
                            </div>
                        ) : (
                            <div className="">
                                <Label className="mb-0 h-full w-full p-9 text-center">
                                    Click to upload or drag and drop an image
                                </Label>
                                <input
                                    {...getInputProps()}
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ display: "none" }}
                                    className="absolute h-full w-full"
                                />
                            </div>
                        )}
                    </div>
                )}
            </Dropzone>
            {image && (
                <>
                    <div className="flex items-center gap-1">
                        <Label htmlFor="scale">Scale:</Label>
                        <Slider
                            defaultValue={[1]}
                            min={1}
                            max={2}
                            step={0.01}
                            value={[scale]}
                            onValueChange={handleScale}
                        />
                    </div>
                    <Button
                        disabled={buttonDisabled}
                        onClick={() =>
                            handleSave().then(async (blob: any) => {
                                const file: any = new Blob([blob], {
                                    type: blob.type,
                                });
                                file.name = "example.webp";
                                file.lastModified = new Date();
                                setButtonDisabled(true);
                                if (impersonate) {
                                    toast.promise(
                                        Impersonate.SetProfilePic(user, file),
                                        {
                                            loading:
                                                "Updating profile image...",
                                            success: async (data) => {
                                                if (reload) {
                                                    window.location.reload();
                                                } else {
                                                    const body =
                                                        await data.json();
                                                    window.document
                                                        .getElementById(
                                                            "profile-image"
                                                        )
                                                        ?.setAttribute(
                                                            "src",
                                                            "https://cdn.doras.to/doras/user/" +
                                                                body.data
                                                        );
                                                    if (setOpen) {
                                                        setOpen(false);
                                                    }
                                                }
                                                return "Profile image updated";
                                            },
                                            error: (error) => {
                                                setButtonDisabled(false);
                                                return `Failed to update profile image: ${error}`;
                                            },
                                        }
                                    );
                                } else {
                                    toast.promise(SetProfilePic(user, file), {
                                        loading: "Updating profile image...",
                                        success: async (data) => {
                                            if (reload) {
                                                window.location.reload();
                                            } else {
                                                const body = await data.json();
                                                window.document
                                                    .getElementById(
                                                        "profile-image"
                                                    )
                                                    ?.setAttribute(
                                                        "src",
                                                        "https://cdn.doras.to/doras/user/" +
                                                            body.data
                                                    );
                                                if (setOpen) {
                                                    setOpen(false);
                                                }
                                            }
                                            return "Profile image updated";
                                        },
                                        error: (error) => {
                                            setButtonDisabled(false);
                                            return `Failed to update profile image: ${error}`;
                                        },
                                    });
                                }
                            })
                        }
                    >
                        Upload
                    </Button>
                </>
            )}
        </div>
    );
};

export default function ProfileImageUpload({
    user,
    admin,
    className,
    impersonate,
    reload = true,
    customTrigger,
    cc = false,
}: Props) {
    const [open, setOpen] = useState(false);
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {customTrigger ? (
                customTrigger
            ) : (
                <DialogTrigger
                    asChild
                    className={`group relative cursor-pointer rounded-full border-4 border-primary object-cover ${admin ? "w-16" : "w-24 border-4 md:w-44 md:border-8"} ${className}`}
                >
                    <img
                        loading="lazy"
                        id="profile-image"
                        src={user.pic}
                        className={`object-cover ${admin ? "w-16" : "w-24 border-4 transition-all duration-200 hover:opacity-10 md:w-44 md:border-8"}`}
                        alt=""
                    />
                </DialogTrigger>
            )}
            {cc && <DialogOverlay />}
            <DialogContent className={cn(cc && "p-3 sm:h-auto")}>
                <DialogHeader>
                    <DialogTitle>Upload Profile Image</DialogTitle>
                </DialogHeader>
                <div className="relative mx-auto px-2">
                    <ImageCropper
                        user={user}
                        impersonate={impersonate}
                        reload={reload}
                        setOpen={setOpen}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}
